// A customer at a glance (customers.glance): who they are, how long they've been with you, their visits,
// what they've been billed, their membership, what's open and what's at the address. In a chat it's a
// card to the chat's rules: five facts at most, two actions, nothing that scrolls inside it.
import type { CustomerGlanceProps } from '../schemas.ts';
import type { ViewProps } from '../host.ts';
import { day, money, plural } from '../format.ts';
import { Action, Facts, Label, Section } from '../primitives.tsx';

export function CustomerGlance({ data: g, host }: ViewProps<CustomerGlanceProps>) {
  const chat = host.surface === 'chat';
  const since = g.since.slice(0, 4);
  const est = g.openEstimates.listed[0];
  const eq = g.equipment.listed[0];
  const facts: [string, React.ReactNode][] = [];
  if (g.membership)
    facts.push([
      'Member',
      <>
        {g.membership.name}, renews {day(g.membership.endsOn)}
        {!g.membership.autoRenew && (
          <>
            {' '}
            <Label kind="wait">not on auto-renew</Label>
          </>
        )}
      </>,
    ]);
  if (est)
    facts.push([
      g.openEstimates.count > 1 ? `Estimates (${g.openEstimates.count})` : 'Estimate',
      `${est.number}: ${est.title}`,
    ]);
  facts.push([
    'Visits',
    g.jobs.count === 0
      ? 'None yet'
      : `${plural(g.jobs.count, 'job')}${g.jobs.lastVisitOn ? `, last ${day(g.jobs.lastVisitOn)}` : ''}`,
  ]);
  if (g.billedCents != null) facts.push(['Billed', money(g.billedCents)]);
  if (eq)
    facts.push([
      g.equipment.count > 1 ? `Equipment (${g.equipment.count})` : 'Equipment',
      `${eq.kind}${eq.make ? `, ${eq.make}` : ''}${eq.installedOn ? `, ${eq.installedOn.slice(0, 4)}` : ''}`,
    ]);
  if (g.siteContact) facts.push(['Site contact', g.siteContact.name]);
  if (g.about) facts.push(['Note', g.about]);
  if (g.firstCampaign && !chat) facts.push(['First came from', g.firstCampaign.name]);

  const shown = chat ? facts.slice(0, 5) : facts;
  return (
    <article className="flw-glance" aria-label={`${g.name} at a glance`}>
      <h3 className="flw-title">{g.name}</h3>
      <p className="flw-sub">
        {g.address ? `${g.address.line1}, ${g.address.city}. ` : ''}Customer since {since}
      </p>
      {chat ? (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <Facts rows={shown} />
        </div>
      ) : (
        <>
          <Section title="At a glance">
            <Facts rows={shown} />
          </Section>
          {g.notes.length > 0 && (
            <Section title="Latest notes" count={g.notes.length}>
              <ul className="flw-rows">
                {g.notes.map((n, i) => (
                  <li key={i} className="flw-row">
                    <span>{n.body}</span>
                    <span className="flw-muted">{day(n.on)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
      <div className="flw-acts">
        <Action primary onClick={() => host.open({ kind: 'book', customerId: g.customerId })}>
          Book a visit
        </Action>
        <Action onClick={() => host.open({ kind: 'customer', id: g.customerId })}>
          {chat ? 'Open in Flowology' : 'Their record'}
        </Action>
      </div>
    </article>
  );
}
